import { Capacitor } from '@capacitor/core';

/**
 * Recover a tab that outlived a deploy.
 *
 * Every build gives its lazy chunks new hashed names (`web-DSULflqt.js`). A tab
 * opened before a deploy still holds the OLD index.html, so the first time it
 * reaches a lazy import it asks for a chunk the new deploy no longer has, and
 * the browser throws "Failed to fetch dynamically imported module". Nothing is
 * wrong with the code — the page is just stale. Reloading fetches the current
 * index.html and the import succeeds.
 *
 * The deploy also keeps old chunks in the bucket now (no `rsync -d`), which
 * covers most of this. This is the backstop for the rest: CDN edges, chunks
 * deleted before that change, and a user who leaves a tab open across many
 * releases.
 *
 * Native is skipped: the bundle ships inside the app (or the live-update
 * plugin), so a missing chunk there is a real bug, not a stale page, and a
 * reload would only hide it.
 */

const RELOAD_KEY = 'staleChunkReloadAt';

let reloading = false;

/**
 * True once a reload is under way. A handled preload error makes Vite resolve
 * the import with `undefined`, so the caller can throw once more before the
 * page unloads — the reporter checks this to stay quiet about that echo.
 */
export function isReloadingForStaleChunk(): boolean {
  return reloading;
}

/**
 * A second failure inside this window means the reload did not help — the
 * chunk is genuinely missing. Stop reloading and let the error reach the
 * reporter, rather than trapping the user in a reload loop.
 */
const RELOAD_GUARD_MS = 30_000;

/** Chrome, Firefox and Safari word the same failure three different ways. */
const STALE_CHUNK_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
];

export function isStaleChunkError(value: unknown): boolean {
  const message =
    value instanceof Error
      ? value.message
      : typeof value === 'string'
        ? value
        : typeof (value as { message?: unknown })?.message === 'string'
          ? (value as { message: string }).message
          : '';
  return STALE_CHUNK_PATTERNS.some((p) => p.test(message));
}

function readLastReload(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    return 0;
  }
}

/**
 * Reload once. Returns false when a reload already happened recently, so the
 * caller leaves the error alone to be reported.
 */
export function reloadForStaleChunk(now = Date.now()): boolean {
  if (now - readLastReload() < RELOAD_GUARD_MS) return false;
  try {
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    // Storage blocked (private mode). Without the guard a genuinely missing
    // chunk would reload forever, so do not reload at all.
    return false;
  }
  reloading = true;
  window.location.reload();
  return true;
}

/**
 * Install before `initErrorReporting`, so a handled rejection is already
 * marked `defaultPrevented` by the time the reporter sees it.
 */
export function initStaleChunkReload(): void {
  if (Capacitor.isNativePlatform()) return;

  // Vite fires this for any failed lazy import or CSS preload. preventDefault
  // stops Vite rethrowing, so no unhandledrejection follows.
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForStaleChunk()) event.preventDefault();
  });

  // Backstop for an import Vite did not wrap in its preload helper.
  window.addEventListener('unhandledrejection', (event) => {
    if (isStaleChunkError(event.reason) && reloadForStaleChunk()) {
      event.preventDefault();
    }
  });
}
